'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import type { Address, Hex } from 'viem';
import { buttonVariants, CopyButton, Text } from '@repo/ui';
import { ArrowDiagonalUp } from '@repo/ui/icons';
import { abbreviateAddress, formatUint256Percent } from '@repo/utils';
import { EntityHoverCard, EntityHoverCardContent, EntityHoverCardDivider, EntityHoverCardRow, EntityHoverCardRows, EntityHoverCardTrigger } from './EntityHoverCard';
import { MorphoLink } from './MorphoLink';
import { TokenPair } from './TokenPair';
interface MarketHoverCardProps {
  chainId: number;
  marketId: Hex;
  marketParams: {
    loanToken: {
      address?: Address;
      symbol: string;
    };
    collateralToken: {
      address?: Address;
      symbol: string;
    };
    lltv?: bigint;
    irm?: Address;
    oracle?: Address;
  };
  children: ReactNode;
}

/** @internal */
export function MarketHoverCard(t0) {
  const $ = _c(43);
  const {
    chainId,
    marketId,
    marketParams: t1,
    children
  } = t0;
  const {
    loanToken,
    collateralToken,
    lltv,
    irm,
    oracle
  } = t1;
  const pair = `${loanToken.symbol} / ${collateralToken.symbol}`;
  let t2;
  if ($[0] !== marketId) {
    t2 = abbreviateAddress(marketId, 6);
    $[0] = marketId;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const abbreviatedId = t2;
  const t3 = `Market details for ${pair}`;
  let t4;
  if ($[2] !== children || $[3] !== t3) {
    t4 = <EntityHoverCardTrigger asChild={true}><div tabIndex={0} aria-label={t3} className="hover:bg-muted data-[state=open]:bg-muted focus-visible:ring-ring/50 inline-flex w-fit cursor-default items-center rounded-md px-2 py-1 transition-colors outline-none focus-visible:ring-[3px]">{children}</div></EntityHoverCardTrigger>;
    $[2] = children;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== collateralToken || $[6] !== loanToken) {
    t5 = <TokenPair collateralToken={collateralToken} loanToken={loanToken} variant="overlapping" size="md" />;
    $[5] = collateralToken;
    $[6] = loanToken;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== pair) {
    t6 = <Text size="md" className="min-w-0 flex-1 truncate font-semibold">{pair}</Text>;
    $[8] = pair;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = buttonVariants({
      variant: "ghost",
      size: "iconSm"
    });
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = `Open ${pair} market on Morpho`;
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ArrowDiagonalUp className="text-muted-foreground size-5" />;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== chainId || $[13] !== marketId || $[14] !== t8) {
    t10 = <MorphoLink chainId={chainId} marketId={marketId} className={t7} aria-label={t8}>{t9}</MorphoLink>;
    $[12] = chainId;
    $[13] = marketId;
    $[14] = t8;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t5 || $[18] !== t6) {
    t11 = <div className="flex items-center gap-2">{t5}{t6}{t10}</div>;
    $[16] = t10;
    $[17] = t5;
    $[18] = t6;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <EntityHoverCardDivider />;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const t13 = `Market details for ${pair}`;
  const t14 = `Copy market ID ${abbreviatedId}`;
  let t15;
  if ($[21] !== abbreviatedId || $[22] !== marketId || $[23] !== t14) {
    t15 = <EntityHoverCardRow label="Market ID"><CopyButton value={marketId} variant="ghost" size="sm" className="text-foreground -mr-2.5 text-xs" successMessage="Copied market ID to clipboard" label={t14}>{abbreviatedId}</CopyButton></EntityHoverCardRow>;
    $[21] = abbreviatedId;
    $[22] = marketId;
    $[23] = t14;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== lltv) {
    t16 = lltv !== undefined && <EntityHoverCardRow label="LLTV"><Text size="xs">{formatUint256Percent(lltv)}</Text></EntityHoverCardRow>;
    $[25] = lltv;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== irm) {
    t17 = irm && <EntityHoverCardRow label="IRM"><CopyButton value={irm} variant="ghost" size="sm" className="text-foreground -mr-2.5 text-xs" successMessage="Copied IRM address to clipboard" label={`Copy IRM address ${abbreviateAddress(irm)}`}>{abbreviateAddress(irm)}</CopyButton></EntityHoverCardRow>;
    $[27] = irm;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== oracle) {
    t18 = oracle && <EntityHoverCardRow label="Oracle"><CopyButton value={oracle} variant="ghost" size="sm" className="text-foreground -mr-2.5 text-xs" successMessage="Copied oracle address to clipboard" label={`Copy oracle address ${abbreviateAddress(oracle)}`}>{abbreviateAddress(oracle)}</CopyButton></EntityHoverCardRow>;
    $[29] = oracle;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== t13 || $[32] !== t15 || $[33] !== t16 || $[34] !== t17 || $[35] !== t18) {
    t19 = <EntityHoverCardRows aria-label={t13}>{t15}{t16}{t17}{t18}</EntityHoverCardRows>;
    $[31] = t13;
    $[32] = t15;
    $[33] = t16;
    $[34] = t17;
    $[35] = t18;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== t11 || $[38] !== t19) {
    t20 = <EntityHoverCardContent className="w-80">{t11}{t12}{t19}</EntityHoverCardContent>;
    $[37] = t11;
    $[38] = t19;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] !== t20 || $[41] !== t4) {
    t21 = <EntityHoverCard>{t4}{t20}</EntityHoverCard>;
    $[40] = t20;
    $[41] = t4;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  return t21;
}